import React from 'react';
import styled, { css } from 'styled-components';

export type EmptyStateSize = 'small' | 'medium' | 'large';

export type EmptyStateProps = {
  /**
   * The title of the empty state.
   */
  title: string;
  /**
   * The description of the empty state.
   */
  description?: string;
  /**
   * The image/icon to be displayed in the empty state.
   */
  image?: React.ReactNode;
  /**
   * The primary action of the empty state.
   */
  primaryAction?: React.ReactNode;
  /**
   * The secondary action of the empty state.
   */
  secondaryAction?: React.ReactNode;
  /**
   * The size variant of the empty state
   * @default 'medium'
   */
  size?: EmptyStateSize;
  /**
   * Additional className for custom styling
   */
  className?: string;
  /**
   * Whether to center the content vertically
   * @default false
   */
  centerVertically?: boolean;
};

const StyledEmptyState = styled.div<{
  $size: EmptyStateSize;
  $centerVertically: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ $size }) => ($size === 'small' ? '12px' : '16px')};
  padding: var(--spacing-03);
  width: 100%;
  ${({ $centerVertically }) =>
    $centerVertically &&
    css`
      justify-content: center;
      height: 100%;
    `};
`;

const Title = styled.h2<{ $size: EmptyStateSize }>`
  margin: 0;
  font-size: ${({ $size }) =>
    $size === 'small'
      ? 'var(--font-size-sm)'
      : $size === 'medium'
        ? 'var(--font-size-base)'
        : 'var(--font-size-lg)'};
  line-height: var(--line-height-01);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-00);
  color: var(--gray-60);
  text-align: center;
`;

const Description = styled.p<{ $size: EmptyStateSize }>`
  margin: 0;
  font-size: ${({ $size }) =>
    $size === 'small'
      ? 'var(--font-size-sm)'
      : $size === 'medium'
        ? 'var(--font-size-base)'
        : 'var(--font-size-lg)'};
  line-height: var(--line-height-01);
  color: var(--gray-60);
  text-align: center;
`;

const ImageContainer = styled.div<{ $size: EmptyStateSize }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ $size }) => ($size === 'small' ? '8px' : '12px')};
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

export function EmptyState({
  title,
  description,
  image,
  primaryAction,
  secondaryAction,
  size = 'medium',
  className,
  centerVertically = false,
}: EmptyStateProps) {
  return (
    <StyledEmptyState
      className={className}
      $size={size}
      $centerVertically={centerVertically}
    >
      {image && <ImageContainer $size={size}>{image}</ImageContainer>}
      <Title $size={size}>{title}</Title>
      {description && <Description $size={size}>{description}</Description>}
      {(primaryAction || secondaryAction) && (
        <ActionsContainer>
          {primaryAction}
          {secondaryAction}
        </ActionsContainer>
      )}
    </StyledEmptyState>
  );
}
